import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, RefreshControl, Alert } from 'react-native';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AsyncStorage from '../AsyncStorageShim';
import ListsManager, { MessageSource } from '../services/ListsManager';
import { useTranslation } from 'react-i18next';
import TweetActionsManager from '../services/TweetActionsManager';
import TweetCell from '../components/TweetCell';
import Modal from '../components/Modal';
import ReactionModal from '../components/ReactionModal';
import ListUsersScreen from './ListUsersScreen';
import NotificationSettingsScreen from './NotificationSettingsScreen';
import NewListScreen from './NewListScreen';
import FilterScreen from './FilterScreen';
import { FaUsers, FaBell, FaEdit, FaFilter, FaShare, FaTrash, FaDownload, FaTimes, FaArrowLeft, FaSync } from 'react-icons/fa';
import { useOrg } from '../hooks/useOrg';

// Design constants
const ACCENT = '#4DA6FF';
const ACCENT_RGBA = 'rgba(77, 166, 255, 0.1)';
const ACCENT_RGBA_DARK = 'rgba(77, 166, 255, 0.3)';

const ListDetailsScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrg } = useOrg();
  const { t } = useTranslation();
  const { listName: initialListName, onListUpdated, onListDeleted } = location.state || {};

  const [listDetails, setListDetails] = useState(null);
  const [tweets, setTweets] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [activeModal, setActiveModal] = useState('none');

  // Reaction Modal State
  const [showReactionModal, setShowReactionModal] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [reactionText, setReactionText] = useState('');
  const [reactionExplanation, setReactionExplanation] = useState('');
  const [reactionError, setReactionError] = useState(null);
  const [currentReactionTweet, setCurrentReactionTweet] = useState(null);
  const [currentReactionListId, setCurrentReactionListId] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 20; // Smaller page size for smoother scrolling

  // Flag to prevent multiple fetch calls
  const isFetchingRef = useRef(false);

  // Filter state
  const [currentSelectedUsers, setCurrentSelectedUsers] = useState(null);
  const [currentFromDate, setCurrentFromDate] = useState(null);
  const [currentToDate, setCurrentToDate] = useState(null);
  const [currentSelectedSources, setCurrentSelectedSources] = useState([
    MessageSource.TWITTER,
    MessageSource.TELEGRAM,
    MessageSource.TIKTOK,
    MessageSource.INSTAGRAM
  ]);

  // Selection state
  const [selectedTweets, setSelectedTweets] = useState([]);
  const [showNewContentBubble, setShowNewContentBubble] = useState(false);
  const [newContentCount, setNewContentCount] = useState(0);
  // Column width control
  const [columnWidth, setColumnWidth] = useState(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('listColumnWidth') : null;
    return saved ? parseInt(saved, 10) : 600;
  });

  const [userLanguage, setUserLanguage] = useState('en');

  useEffect(() => {
    const fetchUserLanguage = async () => {
      try {
        const language = await ListsManager.getListenerLanguage();
        setUserLanguage(language);
      } catch (error) {
        console.error('Error fetching user language:', error);
      }
    };
    fetchUserLanguage();
  }, []);

  useEffect(() => {
    loadListDetails(true, 0);

    // Check if we're returning from notification settings with updates
    if (location.state && location.state.refreshSettings) {
      const { notificationMode, notificationFrequency } = location.state;
      // Update the list details with the new notification settings
      if (listDetails) {
        setListDetails({
          ...listDetails,
          notification_mode: notificationMode,
          notification_frequency: notificationFrequency
        });
      }

      // Clear the state to prevent applying the same update multiple times
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [id, location]);

  useEffect(() => {
    // Check if we're returning from filter screen with filters applied
    if (location.state?.filterApplied) {
      const {
        selectedUsers,
        fromDate,
        toDate,
        dateFilter,
        selectedSources
      } = location.state;

      // Update filter state
      setCurrentSelectedUsers(selectedUsers);
      setCurrentFromDate(fromDate ? new Date(fromDate) : null);
      setCurrentToDate(toDate ? new Date(toDate) : null);
      setCurrentSelectedSources(selectedSources || []);

      // Apply filters to tweets if needed
      const filteredTweets = tweets.filter(tweet => {
        // Filter by source
        const matchesSource = !selectedSources?.length ||
          selectedSources.includes(tweet.source);

        // Filter by username
        const matchesUser = !selectedUsers?.length ||
          selectedUsers.includes(tweet.username);

        // Filter by date
        let matchesDate = true;
        if (fromDate && toDate && tweet.created_at) {
          const tweetDate = new Date(tweet.created_at);
          matchesDate = tweetDate >= new Date(fromDate) && tweetDate <= new Date(toDate);
        }

        return matchesSource && matchesUser && matchesDate;
      });

      setTweets(filteredTweets);

      // Clear the navigation state to prevent applying filters multiple times
      navigate(location.pathname, { replace: true, state: { listId: id, listName: initialListName } });
    }
  }, [location.state]);

  const handleDraftReaction = async (tweet, listId) => {
    setCurrentReactionTweet(tweet);
    setCurrentReactionListId(listId);

    // Check if profile exists first
    const hasProfile = await ListsManager.hasListenerProfile();
    if (!hasProfile) {
      navigate('/writing-style');
      return;
    }

    // Open modal and start loading
    setShowReactionModal(true);
    setReactionLoading(true);
    setReactionError(null);
    setReactionText('');
    setReactionExplanation('');

    try {
      const result = await ListsManager.generateTweetReaction(
        tweet.tweet_content || tweet.translated_content,
        tweet.username || tweet.author_name || 'unknown',
        tweet.list_name,
        null, // list request
        listId // Pass listId
      );

      if (result && result.success) {
        setReactionText(result.reaction_text);
        setReactionExplanation(result.explanation || '');
      } else {
        setReactionError(result?.error_message || 'Failed to generate reaction. Please set up your writing style profile first.');
      }
    } catch (error) {
      console.error('Error generating reaction:', error);
      setReactionError(error.message || 'Failed to generate reaction');
    } finally {
      setReactionLoading(false);
    }
  };

  const handleRegenerateReaction = () => {
    if (currentReactionTweet) {
      handleDraftReaction(currentReactionTweet, currentReactionListId);
    }
  };

  const showNewContentNotification = (count) => {
    setNewContentCount(count);
    setShowNewContentBubble(true);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      setShowNewContentBubble(false);
    }, 5000);
  };

  const loadListDetails = async (isInitialLoad = true, pageNum = 0) => {
    // Prevent multiple simultaneous fetch calls
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      if (isInitialLoad) {
        setLoading(true);
        setError(null);
        setPage(0);

        // Fetch all lists and find the one we need
        const lists = await ListsManager.fetchLists(activeOrg?.id);
        const details = lists.find(list => list.id === id);

        if (!details) {
          throw new Error('List not found');
        }

        setListDetails(details);
      } else {
        setLoadingMore(true);
      }

      const offset = pageNum * ITEMS_PER_PAGE;
      // Calculate since date for refreshing
      const since = refreshing ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : null; // 7 days ago for refresh

      console.log(`Fetching data for page ${pageNum}, offset: ${offset}, items per page: ${ITEMS_PER_PAGE}`);

      // Fetch tweets, Telegram, TikTok, and Instagram messages in parallel with pagination
      const [listTweets, listTelegramMessages, listTiktoks, listInstagrams, listFbGroups] = await Promise.all([
        ListsManager.fetchRelevantTweetsFromList(id, since, ITEMS_PER_PAGE, offset),
        ListsManager.getTelegramMessages(id, since ? since.toISOString() : null, ITEMS_PER_PAGE, offset),
        ListsManager.fetchRelevantTiktoksFromList(id, since, ITEMS_PER_PAGE, offset),
        ListsManager.fetchRelevantInstagramsFromList(id, since, ITEMS_PER_PAGE, offset),
        ListsManager.fetchFacebookGroupPostsFromList(id, since, ITEMS_PER_PAGE, offset),
      ]);

      console.log(`[ListDetailsScreen] id=${id} | tweets=${(listTweets||[]).length} | telegram=${(listTelegramMessages||[]).length} | tiktoks=${(listTiktoks||[]).length} | instagrams=${(listInstagrams||[]).length} | fb=${(listFbGroups||[]).length}`);

      // Combine tweets, Telegram, TikTok, Instagram, and Facebook Group messages
      let combinedMessages = [...(listTweets || []), ...(listTelegramMessages || []), ...(listTiktoks || []), ...(listInstagrams || []), ...(listFbGroups || [])];

      // Sort by creation date (most recent first)
      combinedMessages.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
      });

      console.log(`Fetched ${combinedMessages.length} messages`);

      if (refreshing && tweets.length > 0 && combinedMessages.length > 0) {
        // If refreshing, check for new content
        const existingIds = new Set(tweets.map(t => t.tweet_id || t.id));
        const newContent = combinedMessages.filter(msg => !existingIds.has(msg.tweet_id || msg.id));

        if (newContent.length > 0) {
          setTweets(prevTweets => [...newContent, ...prevTweets]);
          showNewContentNotification(newContent.length);
        }
      } else if (!isInitialLoad) {
        // If loading more content, append to existing tweets
        setTweets(prevTweets => [...prevTweets, ...combinedMessages]);
      } else {
        // Initial load
        setTweets(combinedMessages || []);
      }

      // Update the page number if this wasn't a refresh
      if (!refreshing && !isInitialLoad) {
        setPage(pageNum);
      }

      // Check if we've reached the end
      setHasMore(combinedMessages.length >= ITEMS_PER_PAGE);

    } catch (error) {
      console.error('Error loading list details:', error);
      setError('Failed to load list details');
    } finally {
      setRefreshing(false);
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadListDetails(true, 0);
  };

  // Handle scroll to load more
  const handleLoadMore = () => {
    if (!loadingMore && hasMore && !isFetchingRef.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadListDetails(false, nextPage);
    }
  };

  // Handle scroll event for scroll container
  const handleScroll = (event) => {
    const target = event.target;
    // Load more when within 300px of the bottom
    const scrollPosition = target.scrollHeight - target.scrollTop;
    const threshold = target.clientHeight + 300;

    if (scrollPosition <= threshold && !loadingMore && hasMore && !isFetchingRef.current) {
      handleLoadMore();
    }
  };

  const applyFilters = (filters, currentTweets) => {
    const {
      selectedUsers,
      fromDate,
      toDate,
      selectedSources
    } = filters;

    // Update filter state
    setCurrentSelectedUsers(selectedUsers);
    setCurrentFromDate(fromDate ? new Date(fromDate) : null);
    setCurrentToDate(toDate ? new Date(toDate) : null);
    setCurrentSelectedSources(selectedSources || []);

    // Apply filters to tweets if needed
    const filteredTweets = currentTweets.filter(tweet => {
      // Filter by source
      const matchesSource = !selectedSources?.length ||
        selectedSources.includes(tweet.source);

      // Filter by username
      const matchesUser = !selectedUsers?.length ||
        selectedUsers.includes(tweet.username);

      // Filter by date
      let matchesDate = true;
      if (fromDate && toDate && tweet.created_at) {
        const tweetDate = new Date(tweet.created_at);
        matchesDate = tweetDate >= new Date(fromDate) && tweetDate <= new Date(toDate);
      }

      return matchesSource && matchesUser && matchesDate;
    });

    setTweets(filteredTweets);
  };

  const handleModalClose = (result) => {
    setActiveModal('none');
    if (result === true) {
      // Refresh
      loadListDetails(true, 0);
    } else if (result && result.filterApplied) {
      applyFilters(result, tweets);
    } else if (result && result.refreshSettings) {
      // Handle notification settings refresh
      const { notificationMode, notificationFrequency } = result;
      if (listDetails) {
        setListDetails({
          ...listDetails,
          notification_mode: notificationMode,
          notification_frequency: notificationFrequency
        });
      }
    }
  };

  const handleViewUsers = () => {
    setActiveModal('users');
  };

  const handleNotificationSettings = () => {
    setActiveModal('notifications');
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError('');

    try {
      const success = await ListsManager.deleteList(id);

      if (success) {
        // Navigate back to my-lists with refresh flag
        navigate('/my-lists', { state: { refreshLists: true } });
      } else {
        setDeleteError('Failed to delete list. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting list:', err);
      setDeleteError('An error occurred while deleting the list');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditList = () => {
    setActiveModal('edit');
  };

  const handleFilterContent = () => {
    setActiveModal('filter');
  };

  const handleShareList = () => {
    if (navigator.share) {
      navigator.share({
        title: `ReactFirst AI List: ${listDetails?.name || initialListName}`,
        text: `Check out this list on ReactFirst AI: ${listDetails?.name || initialListName}`,
        url: window.location.href
      }).catch(error => console.log('Error sharing:', error));
    } else {
      alert("Sharing is not available in your browser");
    }
  };

  const handleSelectAll = () => {
    setSelectedTweets([...tweets]);
  };

  const handleClearSelection = () => {
    setSelectedTweets([]);
  };

  const handleExportSelection = () => {
    const messageContent = selectedTweets.map(message => {
      const source = message.source === MessageSource.TELEGRAM ? 'Telegram' : 'Twitter';
      const authorName = message.author_name || message.username || 'Unknown';
      const content = message.translated_content || message.tweet_content || message.content || 'No content';

      return `${source} message by ${authorName}:\n${content}\n\nURL: ${message.url || 'No URL'}\n\n`;
    }).join('---\n\n');

    const exportText = `Exported Messages from List: ${listDetails?.name || initialListName}\n\n${messageContent}`;

    // Create a download link
    const element = document.createElement("a");
    const file = new Blob([exportText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `reactfirst-list-${id}-export.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading && tweets.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={ACCENT} />
        <Text style={styles.loadingText}>{t('app.lists.loadingDetails')}</Text>
      </View>
    );
  }

  return (
    <div className="animate-fade-in" style={{
      height: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--bg-deep-void)',
      backgroundImage: `
        radial-gradient(circle at 15% 50%, rgba(59, 130, 246, 0.08), transparent 25%),
        radial-gradient(circle at 85% 30%, rgba(139, 92, 246, 0.08), transparent 25%)
      `,
      overflow: 'auto', // Changed from hidden to auto to enable screen scrolling
      position: 'relative'
    }}>
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButtonContainer}
            onPress={handleBack}
          >
            <View style={styles.backButtonContent}>
              <FaArrowLeft style={styles.actionIcon} />
              <Text style={styles.backButton}>{t('app.lists.back')}</Text>
            </View>
          </TouchableOpacity>
          {/* Title removed as per user request */}
          <View style={styles.titleContainer}>
            <Text style={styles.headerTitle}>
              {listDetails?.name || initialListName}
            </Text>
            {tweets.length > 0 && (
              <View style={styles.countBadge}>
                <Text style={styles.countText}>{tweets.length}</Text>
              </View>
            )}
          </View>
          <View style={{ width: 70 }} />
        </View>

        {error ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : deleteError ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{deleteError}</Text>
          </View>
        ) : (
          <>
            {/* Main Content Area */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingBottom: 20,
                width: '100%',
                zIndex: 1,
                position: 'relative'
              }}
            >
              {/* Content Container (Header Info + List) */}
              <div style={{
                width: '100%',
                maxWidth: `${columnWidth}px`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                padding: '0 10px'
              }}>

                {/* Info Container with dynamic width */}
                <View style={[styles.listInfoContainer, { margin: 0, marginBottom: 20, width: '100%' }]}>
                  {listDetails && (
                    <>
                      <Text style={{
                        color: 'var(--text-primary)',
                        fontSize: 24,
                        fontWeight: '700',
                        fontFamily: 'Outfit, sans-serif',
                        marginBottom: 12,
                        letterSpacing: '-0.02em',
                        textShadow: '0 0 20px rgba(59, 130, 246, 0.3)'
                      }}>
                        {listDetails?.name || initialListName}
                      </Text>
                      <Text style={styles.listDescription}>{listDetails.request}</Text>
                      <View style={styles.sliderRow}>
                        <Text style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: '500', marginRight: 8 }}>{t('app.lists.columnWidth')}</Text>
                        <input
                          type="range"
                          min="300"
                          max="800"
                          value={columnWidth}
                          onChange={(e) => {
                            const w = parseInt(e.target.value, 10);
                            setColumnWidth(w);
                            if (typeof localStorage !== 'undefined') {
                              localStorage.setItem('listColumnWidth', w.toString());
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </View>
                    </>
                  )}

                  <View style={styles.actionButtonsContainer}>
                    <View style={styles.actionButtonsRow}>
                      <TouchableOpacity style={styles.actionButton} onPress={handleViewUsers}>
                        <FaUsers style={styles.actionIcon} />
                        <Text style={styles.actionButtonText}>{t('app.lists.users')}</Text>
                      </TouchableOpacity>

                      <TouchableOpacity style={styles.actionButton} onPress={handleNotificationSettings}>
                        <FaBell style={styles.actionIcon} />
                        <Text style={styles.actionButtonText}>{t('app.lists.notify')}</Text>
                      </TouchableOpacity>

                      <TouchableOpacity style={styles.actionButton} onPress={handleEditList}>
                        <FaEdit style={styles.actionIcon} />
                        <Text style={styles.actionButtonText}>{t('app.lists.edit')}</Text>
                      </TouchableOpacity>

                      <TouchableOpacity style={styles.actionButton} onPress={handleFilterContent}>
                        <FaFilter style={styles.actionIcon} />
                        <Text style={styles.actionButtonText}>{t('app.lists.filter')}</Text>
                      </TouchableOpacity>

                      <TouchableOpacity style={styles.actionButton} onPress={handleShareList}>
                        <FaShare style={styles.actionIcon} />
                        <Text style={styles.actionButtonText}>{t('app.lists.share')}</Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={[styles.actionButton, styles.deleteButton]}
                        onPress={handleDelete}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <ActivityIndicator size="small" color="#ff6666" />
                        ) : (
                          <>
                            <FaTrash style={styles.deleteIcon} />
                            <Text style={styles.deleteButtonText}>{t('app.lists.delete')}</Text>
                          </>
                        )}
                      </TouchableOpacity>
                    </View>
                  </View>

                  {selectedTweets.length > 0 && (
                    <View style={styles.selectionActionsContainer}>
                      <Text style={styles.selectionText}>{t('app.lists.selected', { count: selectedTweets.length })}</Text>
                      <View style={styles.selectionButtonsContainer}>
                        <TouchableOpacity style={styles.selectionButton} onPress={handleExportSelection}>
                          <FaDownload style={styles.selectionButtonIcon} />
                          <Text style={styles.selectionButtonText}>{t('app.lists.export')}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.selectionButton, styles.clearButton]} onPress={handleClearSelection}>
                          <FaTimes style={styles.selectionButtonIcon} />
                          <Text style={styles.selectionButtonText}>{t('app.lists.clear')}</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  )}
                </View>

                <View style={styles.divider} />

                {showNewContentBubble && (
                  <TouchableOpacity
                    style={[styles.newContentBubble, { alignSelf: 'center', marginBottom: 15 }]}
                    onPress={() => setShowNewContentBubble(false)}
                  >
                    <Text style={styles.newContentBubbleText}>
                      {newContentCount} new {newContentCount === 1 ? 'message' : 'messages'} available
                    </Text>
                  </TouchableOpacity>
                )}

                {/* List Content */}
                <div
                  style={{
                    flex: 1,
                    width: '100%',
                    border: '1px solid var(--border, rgba(255,255,255,0.08))',
                    borderRadius: '24px',
                    background: 'var(--bg-card)',
                    backdropFilter: 'blur(12px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minHeight: 0 // Important for nested flex scroll
                  }}
                >
                  {/* Internal header removed to avoid duplication */}

                  {/* Column content */}
                  <div
                    className="messages-container"
                    style={{
                      padding: '5px',
                      display: 'flex',
                      flexDirection: 'column',
                      overflowY: 'auto', // Enable scrolling
                      flex: 1, // Fill available space
                      scrollBehavior: 'smooth'
                    }}
                  >
                    {/* NOTE: No longer scrollable here, outer container scrolls */}
                    {refreshing && (
                      <div style={{
                        padding: '10px',
                        textAlign: 'center',
                        backgroundColor: ACCENT_RGBA,
                        borderRadius: '8px',
                        marginBottom: '10px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <ActivityIndicator size="small" color={ACCENT} />
                          <div style={{ color: ACCENT, marginLeft: '10px' }}>{t('app.lists.refreshing')}</div>
                        </div>
                      </div>
                    )}

                    {!refreshing && (
                      <div
                        style={{
                          padding: '10px',
                          textAlign: 'center',
                          backgroundColor: ACCENT_RGBA,
                          borderRadius: '8px',
                          marginBottom: '10px',
                          cursor: 'pointer'
                        }}
                        onClick={handleRefresh}
                      >
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', color: ACCENT }}>
                          <FaSync style={{ marginRight: '5px' }} /> {t('app.lists.tapToRefresh')}
                        </div>
                      </div>
                    )}

                    {tweets.length > 0 ? (
                      <div>
                        {tweets.map((message, index) => {
                          // Ensure each message has the current list name
                          const messageWithListName = {
                            ...message,
                            list_name: listDetails?.name || initialListName
                          };

                          return (
                            <TweetCell
                              currentUserLanguage={userLanguage}
                              key={`${message.tweet_id || message.id}-${index}`}
                              tweet={messageWithListName}
                              listId={id}
                              onReaction={handleDraftReaction}
                            />
                          );
                        })}

                        {loadingMore && (
                          <div style={{
                            padding: '15px',
                            marginTop: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'row',
                            backgroundColor: 'var(--bg-card)',
                            borderRadius: '8px'
                          }}>
                            <ActivityIndicator size="small" color={ACCENT} />
                            <div style={{
                              color: ACCENT,
                              marginLeft: '10px',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}>
                              {t('app.lists.loadingMore')}
                            </div>
                          </div>
                        )}

                        {!hasMore && tweets.length > 0 && (
                          <div style={{
                            textAlign: 'center',
                            color: 'var(--text-muted)',
                            padding: '15px',
                            marginTop: '15px',
                            marginBottom: '10px',
                            fontSize: '14px',
                            borderTop: '1px solid var(--border, rgba(255,255,255,0.08))',
                            backgroundColor: 'var(--bg-card)'
                          }}>
                            {t('app.lists.endOfList')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingTop: '50px',
                        paddingBottom: '50px'
                      }}>
                        <div style={{
                          color: 'var(--text-muted)',
                          fontSize: '16px',
                          textAlign: 'center'
                        }}>
                          {t('app.lists.noMessages')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </View>

      {/* Modals */}
      <Modal
        isVisible={activeModal === 'users'}
        onClose={() => handleModalClose()}
        title={`${listDetails?.name || initialListName} Users`}
      >
        <div style={{ height: '70vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ListUsersScreen
            listId={id}
            initialListName={listDetails?.name || initialListName}
            onClose={() => handleModalClose()}
            isModal={true}
          />
        </div>
      </Modal>

      <Modal
        isVisible={activeModal === 'notifications'}
        onClose={() => handleModalClose()}
        title={t('app.lists.notificationSettings')}
      >
        <div style={{ height: '70vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <NotificationSettingsScreen
            listId={id}
            listName={listDetails?.name || initialListName}
            notificationMode={listDetails?.notification_mode?.toLowerCase() || 'mute'}
            notificationFrequency={listDetails?.notification_frequency}
            onClose={(result) => handleModalClose(result)}
            isModal={true}
          />
        </div>
      </Modal>

      <Modal
        isVisible={activeModal === 'edit'}
        onClose={() => handleModalClose()}
        title={t('app.lists.editList')}
      >
        <div style={{ height: '70vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <NewListScreen
            isForEditing={true}
            listId={id}
            existingListName={listDetails?.name || initialListName}
            existingListPrompt={listDetails?.request || ''}
            existingListReactionStyle={listDetails?.reaction_writing_style || ''}
            existingIsWideMonitoring={listDetails?.is_wide_monitoring || false}
            existingIsWideMonitoringTiktok={listDetails?.is_wide_monitoring_tiktok || false}
            existingIsWideMonitoringInstagram={listDetails?.is_wide_monitoring_instagram || false}
            existingVisibility={listDetails?.visibility || 'org'}
            onClose={(result) => handleModalClose(result)}
            isModal={true}
          />
        </div>
      </Modal>

      <Modal
        isVisible={activeModal === 'filter'}
        onClose={() => handleModalClose()}
        title={t('app.lists.filterContent')}
      >
        <div style={{ height: '70vh', width: '100%', overflowY: 'auto' }}>
          <FilterScreen
            listId={id}
            selectedUsers={currentSelectedUsers}
            fromDate={currentFromDate?.toISOString()}
            toDate={currentToDate?.toISOString()}
            dateFilter={null}
            selectedSources={currentSelectedSources}
            onClose={(result) => handleModalClose(result)}
            isModal={true}
          />
        </div>
      </Modal>

      <ReactionModal
        isVisible={showReactionModal}
        onClose={() => setShowReactionModal(false)}
        reactionText={reactionText}
        explanation={reactionExplanation}
        isLoading={reactionLoading}
        onRegenerate={handleRegenerateReaction}
        platform={currentReactionTweet?.source === MessageSource.TELEGRAM ? 'telegram' : 'twitter'}
        originalAuthor={currentReactionTweet?.username || currentReactionTweet?.author_name}
        tweetId={currentReactionTweet?.tweet_id || currentReactionTweet?.id}
        error={reactionError}
      />
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'var(--bg-card)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)', // For Safari
    zIndex: 1000,
    position: 'sticky',
    top: 0,
    boxShadow: '0 4px 32px rgba(0, 0, 0, 0.4)',
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  countBadge: {
    backgroundColor: 'rgba(77, 166, 255, 0.15)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(77, 166, 255, 0.3)',
  },
  countText: {
    color: '#4DA6FF',
    fontSize: 12,
    fontWeight: '600',
    fontFamily: 'Outfit, sans-serif',
  },
  backButtonContainer: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  backButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    transition: 'all 0.2s',
  },
  backButton: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 8,
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'Outfit, sans-serif',
    letterSpacing: '-0.01em',
    textShadow: '0 2px 10px rgba(0,0,0,0.3)',
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: 'var(--text-secondary)',
    marginTop: 16,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    color: 'var(--accent-warning)',
    textAlign: 'center',
    fontSize: 16,
  },
  listInfoContainer: {
    padding: 24,
    backgroundColor: 'var(--bg-card)',
    borderRadius: 24,
    marginVertical: 24,
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(12px)',
    boxShadow: 'var(--shadow-glass)',
  },
  listDescription: {
    color: 'var(--text-primary)',
    fontSize: 16,
    marginBottom: 20,
    lineHeight: 24,
    textAlign: 'center',
    opacity: 0.9,
  },
  sliderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    justifyContent: 'center',
    gap: 12,
  },
  actionButtonsContainer: {
    marginVertical: 10,
    width: '100%',
    alignItems: 'center',
  },
  actionButtonsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
  },
  actionButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 16,
    minWidth: 100,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    flexDirection: 'row',
    justifyContent: 'center',
    transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
  },
  actionIcon: {
    color: 'var(--text-secondary)',
    fontSize: 16,
    marginRight: 8,
  },
  actionButtonText: {
    color: 'var(--text-secondary)',
    fontSize: 14,
    fontWeight: '600',
  },
  deleteButton: {
    backgroundColor: 'rgba(244, 63, 94, 0.1)',
    borderColor: 'rgba(244, 63, 94, 0.2)',
  },
  deleteButtonText: {
    color: 'var(--accent-warning)',
    fontWeight: '600',
    fontSize: 14,
  },
  deleteIcon: {
    color: 'var(--accent-warning)',
    fontSize: 16,
    marginRight: 8,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    marginVertical: 16,
    width: '100%',
  },
  newContentBubble: {
    backgroundColor: 'var(--accent-primary)',
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderRadius: 20,
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
  },
  newContentBubbleText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 14,
  },
  selectionActionsContainer: {
    marginTop: 16,
    padding: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
  },
  selectionText: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 12,
  },
  selectionButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  selectionButton: {
    backgroundColor: 'var(--accent-primary)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  clearButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  selectionButtonText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 13,
  },
  selectionButtonIcon: {
    color: 'white',
    fontSize: 14,
    marginRight: 6,
  },
  loadingMoreContainer: {
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  loadingMoreText: {
    color: 'var(--text-secondary)',
    marginLeft: 12,
    fontSize: 14,
  },
  endOfListText: {
    textAlign: 'center',
    color: 'var(--text-muted)',
    padding: 24,
    fontSize: 14,
  },
});

export default ListDetailsScreen;